import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Tag, Plus, Rocket, MoreHorizontal, Sparkles, AlertTriangle } from "lucide-react";
import { EmptyState, GlassCard, T, TYPE, formatDate, primaryButtonSx, ghostButtonSx, labelSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { Progress, TypeIcon, StatusPill, IssueKey, has } from "../ui";
import { Select, dateInput, fieldSx } from "../inputs";

const VersionDialog = ({ state, project, onClose, onSaved }) => {
  const ctx = usePm();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (state) setForm({ name: state.name || "", description: state.description || "", startDate: dateInput(state.startDate), releaseDate: dateInput(state.releaseDate), releaseNotes: state.releaseNotes || "" });
  }, [state]);
  if (!state) return null;
  const save = async () => {
    try {
      const body = { ...form, startDate: form.startDate || null, releaseDate: form.releaseDate || null };
      if (state._id) await pm.updateVersion(state._id, body);
      else await pm.createVersion(project._id, body);
      onSaved();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state._id ? `Edit ${state.name}` : "New version"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField size="small" label="Name" placeholder="v1.4.0" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} />
          <TextField size="small" multiline minRows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={fieldSx} />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} sx={fieldSx} />
            <TextField fullWidth size="small" type="date" label="Release date" InputLabelProps={{ shrink: true }} value={form.releaseDate} onChange={(e) => setForm({ ...form, releaseDate: e.target.value })} sx={fieldSx} />
          </Stack>
          <TextField size="small" multiline minRows={5} label="Release notes" value={form.releaseNotes} onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })} sx={fieldSx} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!form.name} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const ReleaseDialog = ({ state, versions, onClose, onDone }) => {
  const ctx = usePm();
  const [moveTo, setMoveTo] = useState("");
  const [check, setCheck] = useState(null);
  useEffect(() => {
    if (!state) return;
    setMoveTo("");
    setCheck(null);
    pm.releaseVersion(state._id, {}).then((r) => {
      if (r.released) {
        ctx.notify(`${state.name} released`);
        onDone();
      } else setCheck(r);
    }).catch((err) => { ctx.fail(err); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  if (!state) return null;
  const others = versions.filter((v) => v.status === "unreleased" && v._id !== state._id);
  const release = async (body) => {
    try {
      await pm.releaseVersion(state._id, body);
      ctx.notify(`${state.name} released`);
      onDone();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Release {state.name}</DialogTitle>
      <DialogContent dividers>
        {!check ? <CircularProgress size={20} /> : (
          <>
            <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mb: 2 }}>{check.warning}</Alert>
            <Box sx={{ maxHeight: 200, overflowY: "auto", mb: 2 }}>{check.unresolved.map((i) => <Typography key={i._id} sx={{ fontSize: TYPE.small }}><b>{i.key}</b> {i.summary}</Typography>)}</Box>
            {others.length > 0 && <Select label="Move unresolved issues to" value={moveTo} onChange={setMoveTo} allowEmpty emptyLabel="Keep them in this version" options={others.map((v) => ({ value: v._id, label: v.name }))} />}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        {check && <Button variant="contained" sx={primaryButtonSx} onClick={() => release(moveTo ? { moveUnresolvedTo: moveTo } : { force: true })}>{moveTo ? "Move & release" : "Release anyway"}</Button>}
      </DialogActions>
    </Dialog>
  );
};

const VersionIssues = ({ project, version }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    pm.projectIssues(project._id, { all: [{ field: "fixVersion", op: "eq", value: String(version._id) }] }, { limit: 200, sort: "status" }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [project._id, version._id]);
  if (!rows) return <CircularProgress size={18} />;
  if (!rows.length) return <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No issues have this fix version yet.</Typography>;
  return rows.map((i) => (
    <Stack key={i._id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
      <TypeIcon typeId={i.typeId} size={14} /><IssueKey issue={i} projectKey={project.key} />
      <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{i.summary}</Typography>
      <StatusPill statusId={i.statusId} />
    </Stack>
  ));
};

const Releases = ({ project, version: refreshKey }) => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [menu, setMenu] = useState(null);
  const [notes, setNotes] = useState({});
  const load = useCallback(() => pm.versions(project._id).then(setRows).catch((err) => setError(errorMessage(err))), [project._id]);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const can = (p) => has(project, p) && !project.archivedAt;
  const genNotes = async (v) => {
    setNotes((n) => ({ ...n, [v._id]: { busy: true } }));
    try {
      const r = await pm.releaseNotesAi(v._id);
      setNotes((n) => ({ ...n, [v._id]: r }));
    } catch (err) {
      setNotes((n) => ({ ...n, [v._id]: { error: errorMessage(err) } }));
    }
  };
  const notesText = (g) => [g.title, "", g.summary, "", ...(g.features?.length ? ["Features:", ...g.features.map((x) => `• ${x}`), ""] : []), ...(g.fixes?.length ? ["Fixes:", ...g.fixes.map((x) => `• ${x}`), ""] : []), ...(g.improvements?.length ? ["Improvements:", ...g.improvements.map((x) => `• ${x}`)] : [])].join("\n");

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        {can("release.create") && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={14} />} onClick={() => setEdit({})}>Create version</Button>}
      </Stack>
      {!rows.length ? (
        <GlassCard><EmptyState icon={Tag} title="No versions yet" description="Versions group the work that ships together. Set fix versions on issues to track progress." /></GlassCard>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((v) => (
            <GlassCard key={v._id} sx={{ p: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <Box sx={{ minWidth: 200, cursor: "pointer" }} onClick={() => setOpen(open === v._id ? null : v._id)}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Tag size={15} color={v.status === "released" ? "#12B76A" : T.brand} />
                    <Typography sx={{ fontWeight: 700 }}>{v.name}</Typography>
                    <Chip size="small" label={v.status} sx={{ height: 20, fontSize: "0.66rem", bgcolor: v.status === "released" ? "#DCFAE6" : T.track }} />
                    {v.overdue && <Chip size="small" label="Overdue" sx={{ height: 20, fontSize: "0.66rem", bgcolor: "#FEE4E2", color: "#B42318" }} />}
                  </Stack>
                  <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{v.releaseDate ? `${v.status === "released" ? "Released" : "Planned"} ${formatDate(v.releasedAt || v.releaseDate)}` : "No release date"}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}><Progress value={v.progressPct} label={`${v.done}/${v.issues} issues done · ${v.openBugs} open bugs`} color={v.status === "released" ? "#12B76A" : T.brand} /></Box>
                <Stack direction="row" spacing={0.5}>
                  {v.status === "unreleased" && can("release.update") && <Button size="small" startIcon={<Rocket size={14} />} onClick={() => setReleasing(v)}>Release</Button>}
                  {ctx.settings.ai?.enabled && has(project, "ai.use") && <Button size="small" startIcon={<Sparkles size={14} />} onClick={() => { setOpen(v._id); genNotes(v); }}>Notes</Button>}
                  {can("release.update") && <IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, v })}><MoreHorizontal size={16} /></IconButton>}
                </Stack>
              </Stack>
              {open === v._id && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${T.border}`, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                  <Box><Typography sx={{ ...labelSx, mb: 1 }}>Issues</Typography><VersionIssues project={project} version={v} /></Box>
                  <Box>
                    <Typography sx={{ ...labelSx, mb: 1 }}>Release notes</Typography>
                    {notes[v._id]?.busy && <CircularProgress size={18} />}
                    {notes[v._id]?.error && <Alert severity="warning">{notes[v._id].error}</Alert>}
                    {notes[v._id]?.generated ? (
                      <>
                        <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap", p: 1.5, bgcolor: T.surfaceMuted, borderRadius: "8px" }}>{notesText(notes[v._id].generated)}</Typography>
                        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 0.5 }}>AI draft from {notes[v._id].facts.completedIssues} completed issues — review before saving.</Typography>
                        {can("release.update") && <Button size="small" sx={{ mt: 1 }} onClick={async () => { await pm.updateVersion(v._id, { releaseNotes: notesText(notes[v._id].generated) }); ctx.notify("Release notes saved"); load(); }}>Save as release notes</Button>}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap", color: v.releaseNotes ? T.ink : T.inkMuted }}>{v.releaseNotes || "No release notes."}</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </GlassCard>
          ))}
        </Stack>
      )}
      <Menu anchorEl={menu?.el} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setEdit(menu.v); setMenu(null); }}>Edit</MenuItem>
        {menu?.v.status !== "archived" && <MenuItem onClick={async () => { await pm.updateVersion(menu.v._id, { status: "archived" }); setMenu(null); load(); }}>Archive</MenuItem>}
        {menu?.v.status === "archived" && <MenuItem onClick={async () => { await pm.updateVersion(menu.v._id, { status: "unreleased" }); setMenu(null); load(); }}>Unarchive</MenuItem>}
        {has(project, "release.delete") && <MenuItem sx={{ color: "#B42318" }} onClick={async () => { const v = menu.v; setMenu(null); if (window.confirm(`Delete ${v.name}? Issues keep existing; the fix version is removed from them.`)) { await pm.deleteVersion(v._id); load(); } }}>Delete</MenuItem>}
      </Menu>
      <VersionDialog state={edit} project={project} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <ReleaseDialog state={releasing} versions={rows} onClose={() => setReleasing(null)} onDone={() => { setReleasing(null); load(); }} />
    </Box>
  );
};

export default Releases;
